import { getTranslations } from "next-intl/server";
import { Link } from "@/i18n/navigation";
import { SafeImage } from "@/components/ui/safe-image";
import { Facebook, Twitter, Linkedin, Mail, Phone, MapPin, ArrowUpRight, Github, Instagram, Youtube } from "lucide-react";
import type { Settings } from "@/lib/api";
import type { ComponentType } from "react";

export async function Footer({ initialSettings, initialPages }: { initialSettings?: Settings | null, initialPages?: any[] }) {
    const t = await getTranslations("Footer");
    const settings = initialSettings ?? null;

    const rawLinks = initialPages ? initialPages.filter((page: any) => page.slug || page.type).map((page: any) => ({
        name: page.title,
        href: `/p/${page.slug || page.type}`
    })) : [];

    const footerSections = [
        {
            title: t("links.services_title"),
            links: [
                { name: t("links.hazardous"), href: "/#services" },
                { name: t("links.sharps"), href: "/#services" },
                { name: t("links.pharmaceutical"), href: "/#services" },
                { name: t("links.training"), href: "/#services" },
                { name: t("links.emergency"), href: "/#services" },
            ]
        },
        {
            title: t("links.company_title"),
            links: rawLinks.length > 0 ? rawLinks : [
                { name: t("links.about"), href: "/p/about" },
                { name: t("links.process"), href: "/#process" },
                { name: t("links.blog"), href: "/blog" },
                { name: t("links.careers"), href: "/careers" },
                { name: t("links.support"), href: "/support" },
            ]
        }
    ];

    const getSocialIcon = (key: string): ComponentType<{ className?: string }> => {
        switch (key.toLowerCase()) {
            case 'facebook': return Facebook;
            case 'twitter': return Twitter;
            case 'linkedin': return Linkedin;
            case 'github': return Github;
            case 'instagram': return Instagram;
            case 'youtube': return Youtube;
            default: return Facebook;
        }
    };

    const socialLinksData = settings?.social_links
        ? Object.entries(settings.social_links)
            .filter(([, href]) => !!href)
            .map(([key, href]) => ({
                icon: getSocialIcon(key),
                href: href as string,
                label: key
            }))
        : [
            { icon: Facebook, href: "#", label: t("social_facebook") },
            { icon: Twitter, href: "#", label: t("social_twitter") },
            { icon: Linkedin, href: "#", label: t("social_linkedin") },
        ];

    const address = (() => {
        if (!settings?.address) return t("address");
        if (typeof settings.address === 'object') return settings.address.address || t("address");
        return settings.address || t("address");
    })();

    return (
        <footer className="relative bg-slate-950 text-slate-300 py-16 md:py-24 overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute top-0 right-0 w-1/4 h-1/4 bg-primary/5 blur-[120px] rounded-full" />
            
            <div className="container px-4 md:px-6 relative z-10">
                 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-20">
                     {/* Brand Column */}
                     <div className="lg:col-span-4 space-y-8">
                         <Link href="/" className="inline-block transition-transform hover:scale-105 active:scale-95">
                             <SafeImage
                                 src={settings?.logo || "/logo.webp"}
                                 fallbackSrc="/logo.png"
                                 alt={t("logo_alt")}
                                 width={240} 
                                 height={150} 
                                 sizes="(max-width: 768px) 96px, 128px"
                                 className="h-16 w-auto object-contain brightness-110 saturate-110"
                             />
                         </Link>
                         <p className="text-lg text-slate-400 leading-relaxed">
                             {t("description")}
                         </p>
                         <div className="flex gap-4">
                             {socialLinksData.map((social, i) => (
                                 <Link 
                                    key={i} 
                                    href={social.href} 
                                    aria-label={social.label}
                                    className="group relative w-12 h-12 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 hover:bg-secondary hover:border-secondary transition-all duration-500 overflow-hidden"
                                 >
                                     <social.icon className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                                     <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                                 </Link>
                             ))}
                         </div>
                     </div>
                     
                     {/* Links Columns */}
                     <div className="lg:col-span-1 hidden lg:block" /> {/* Spacer */}
                     
                     <div className="lg:col-span-4 grid grid-cols-2 gap-8">
                         {footerSections.map((section, idx) => (
                             <div key={idx}>
                                 <h3 className="font-bold text-white text-xl mb-8 tracking-tight capitalize">
                                     {section.title}
                                 </h3>
                                 <ul className="space-y-4">
                                     {section.links.map((link, i) => (
                                         <li key={i}>
                                             <Link 
                                                href={link.href} 
                                                className="text-slate-400 hover:text-secondary transition-all duration-300 flex items-center group gap-1"
                                             >
                                                 {link.name}
                                                 <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-y-1 translate-x-1 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                                             </Link>
                                         </li>
                                     ))}
                                 </ul>
                             </div>
                         ))}
                     </div>
 
                     {/* Contact Column */}
                     <div className="lg:col-span-3 space-y-8">
                         <h3 className="font-bold text-white text-xl mb-8 tracking-tight">
                             {t("links.contact_title")}
                         </h3>
                         <div className="space-y-6">
                             <div className="flex items-start gap-4 group">
                                 <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all">
                                     <MapPin className="w-5 h-5" />
                                 </div>
                                 <div className="space-y-1">
                                     <span className="block text-xs text-slate-400 font-bold uppercase tracking-wider">{t("address_label")}</span>
                                     <p className="text-sm font-medium leading-relaxed">
                                        {address}
                                     </p>
                                 </div>
                             </div>
                             
                             <div className="flex items-start gap-4 group">
                                 <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all">
                                     <Mail className="w-5 h-5" />
                                 </div>
                                 <div className="space-y-1">
                                     <span className="block text-xs text-slate-400 font-bold uppercase tracking-wider">{t("email_label")}</span>
                                     <a href={`mailto:${settings?.email || t("email")}`} className="text-sm font-medium hover:text-white transition-colors">{settings?.email || t("email")}</a>
                                 </div>
                             </div>

                             <div className="flex items-start gap-4 group">
                                 <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all">
                                     <Phone className="w-5 h-5" />
                                 </div>
                                 <div className="space-y-1">
                                     <span className="block text-xs text-slate-400 font-bold uppercase tracking-wider">{t("phone_label")}</span>
                                     <a href={`tel:${(settings?.phone || t("phone")).replace(/\s/g, '')}`} className="text-sm font-medium hover:text-white transition-colors" dir="ltr">{settings?.phone || t("phone")}</a>
                                 </div>
                             </div>
                         </div>
                     </div>
                 </div>

                 {/* Bottom Bar */}
                 <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
                     <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-slate-400 font-medium">
                         <p>&copy; {new Date().getFullYear()} {t("copyright")}</p>
                         <span className="hidden sm:inline text-slate-700">•</span>
                         <p className="flex items-center gap-1.5 text-xs text-slate-400">
                             <span>{t("developed_by")}</span>
                             <a 
                                 href="https://alnobough.com/" 
                                 target="_blank" 
                                 rel="noopener noreferrer" 
                                 className="text-secondary hover:text-white font-bold transition-colors underline-offset-4 hover:underline"
                             >
                                 {t("developer_name")}
                             </a>
                         </p>
                     </div>
                     
                     <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
                         {[
                             { name: t("links.privacy"), href: "/privacy" },
                             { name: t("links.terms"), href: "/terms" },
                             { name: t("links.delete_account"), href: "/delete-account" },
                             { name: t("links.sitemap"), href: "/sitemap.xml" }
                         ].map((link, i) => (
                             <Link 
                                key={i} 
                                href={link.href} 
                                className="text-sm text-slate-400 hover:text-white transition-colors underline-offset-4 hover:underline"
                             >
                                 {link.name}
                             </Link>
                         ))}
                     </div>
                 </div>
            </div>
        </footer>
    )
}